import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Calendar, Mail, Phone, MapPin, AlertCircle, Check, ExternalLink, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { TennisBookingTutorial } from '../components/TennisBookingTutorial';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
const t = (k, o) => i18n.t(k, o);

export const Tennis = () => {
  const { t } = useTranslation();
  
  return (
    <div data-testid="tennis-page">
      <SEO 
        title={t('pg.Tennis.0c493d')} 
        description={t('pg.Tennis.789ad5')}
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=1920&q=80"
            alt={t('pg.Tennis.0c7b32')}
            loading="eager"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('tennis.department', 'Tennisavdeling')}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('tennis.heroTitle1', 'SPILL DIN')}<br />
            <span className="text-green-400">{t('tennis.heroTitle2', 'BESTE KAMP')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mb-8">
            {t('tennis.heroDescription', 'Velkommen til tennisavdelingen i Veitvet Sportsklubb! Vi har to flotte utendørsbaner og et aktivt fellesskap av tennisentusiaster.')}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/tennis/login" aria-label={t('pg.Tennis.6b39c1')}>
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-600">
                <LogIn className="mr-2 w-5 h-5" aria-hidden="true" />
                {t('tennis.bookCourt', 'Book tennisbane')}
              </Button>
            </Link>
            <a 
              href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('pg.Tennis.1e2e36')}
            >
              <Button variant="outline" className="border-white/30 text-white uppercase tracking-wider px-8 py-6 hover:bg-white/10">
                {t('nav.joinUs', 'Bli medlem')}
                <ExternalLink className="ml-2 w-5 h-5" aria-hidden="true" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-green-500 text-2xl font-bold">2</span>
              </div>
              <p className="text-slate-900 dark:text-white font-bold">{t('tennis.outdoorCourts', 'Utendørsbaner')}</p>
              <p className="text-slate-500 text-sm">{t('pg.Tennis.e4b092')}</p>
            </div>
            <div>
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-green-500 text-2xl font-bold">100+</span>
              </div>
              <p className="text-slate-900 font-bold">{t('pg.Tennis.c1ced8')}</p>
              <p className="text-slate-500 text-sm">{t('pg.Tennis.78ced2')}</p>
            </div>
            <div>
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-6 h-6 text-green-500" />
              </div>
              <p className="text-slate-900 font-bold">{t('pg.Tennis.971479')}</p>
              <p className="text-slate-500 text-sm">{t('pg.Tennis.b94a84')}</p>
            </div>
            <div>
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-green-500" />
              </div>
              <p className="text-slate-900 font-bold">{t('pg.Tennis.efd7f5')}</p>
              <p className="text-slate-500 text-sm">{t('pg.Tennis.631f3b')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Tennis.837d65')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('pg.Tennis.c7e915')}
            </h2>
          </div>

          {/* Online Booking Card */}
          <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-2xl border border-green-500/30 p-8 mb-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="w-10 h-10 text-green-500" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('pg.Tennis.1a9523')}</h3>
                <p className="text-slate-600 mb-4">
                  {t('pg.Tennis.f2e95c')}
                </p>
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  <Link to="/tennis/login">
                    <Button className="bg-green-500 text-white font-bold hover:bg-green-500/90">
                      <LogIn className="mr-2 w-4 h-4" />
                      {t('pg.Tennis.99bc56')}
                    </Button>
                  </Link>
                  <Link to="/tennis/register">
                    <Button variant="outline" className="border-white/30 text-slate-900 hover:bg-slate-100 dark:bg-slate-700">
                      {t('pg.Tennis.c76310')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Step-by-step booking tutorial */}
          <div className="mb-8">
            <TennisBookingTutorial />
          </div>

          {/* Requirements Card */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8">
            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-green-500" />
              {t('pg.Tennis.2c4c3e')}
            </h3>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-slate-900 font-medium">{t('pg.Tennis.88d532')}</p>
                  <p className="text-slate-500 text-sm">{t('pg.Tennis.95f23d')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-slate-900 font-medium">{t('pg.Tennis.36817f')}</p>
                  <p className="text-slate-500 text-sm">{t('pg.Tennis.da8f13')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-slate-900 font-medium">{t('pg.Tennis.6cdaba')}</p>
                  <p className="text-slate-500 text-sm">{t('pg.Tennis.641d77')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-slate-900 font-medium">
                    {t('pg.Tennis.7d4d7d')}
                  </p>
                  <p className="text-slate-500 text-sm">
                    {t('pg.Tennis.1d75a8')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-slate-900 font-medium">{t('pg.Tennis.3800a7')}</p>
                  <p className="text-slate-500 text-sm">
                    {t('pg.Tennis.e63275')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
              <p className="text-slate-600 text-sm">
                <strong className="text-green-500">{t('pg.Tennis.eb87d3')}</strong> {t('pg.Tennis.cf1159')} <strong>{t('pg.Tennis.5c1e54')}</strong> {t('pg.Tennis.fddc98')} <strong>10:00 - 21:00</strong>.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/pricing">
                <Button variant="outline" className="border-slate-300 text-slate-900 hover:bg-slate-100 dark:bg-slate-700">
                  {t('pg.Tennis.f7cd91')}
                </Button>
              </Link>
              <a 
                href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-green-500 text-white font-bold hover:bg-green-500/90">
                  {t('pg.Tennis.8df769')}
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>

          {/* Contact for Booking */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-6">{t('pg.Tennis.825078')}</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-100 rounded-xl p-6">
                <Mail className="w-8 h-8 text-green-500 mb-3" />
                <h4 className="text-slate-900 font-bold mb-2">{t('pg.Tennis.3d4125')}</h4>
                <a 
                  href="mailto:nickolas.paynter@veitvet-sportsklubb.no" 
                  className="text-green-500 hover:underline break-all"
                >
                  nickolas.paynter@veitvet-sportsklubb.no
                </a>
              </div>
              <div className="bg-slate-100 rounded-xl p-6">
                <Phone className="w-8 h-8 text-green-500 mb-3" />
                <h4 className="text-slate-900 font-bold mb-2">{t('pg.Tennis.0975cf')}</h4>
                <a 
                  href="tel:+4790739749" 
                  className="text-green-500 hover:underline text-lg"
                >
                  +47 907 39 749
                </a>
                <p className="text-slate-400 text-sm mt-2">
                  {t('pg.Tennis.dcdae4')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Fees */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.Tennis.a7260e')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('pg.Tennis.79b691')}
            </h2>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden max-w-md mx-auto">
            <div className="bg-green-500/10 p-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xl font-bold text-slate-900 text-center">{t('pg.Tennis.ac3bab')}</h3>
            </div>
            <div className="p-6">
              <table className="w-full">
                <tbody>
                  <tr className="border-b border-white/5">
                    <td className="py-4 text-slate-600 dark:text-slate-300">{t('pg.Tennis.075ed3')}</td>
                    <td className="py-4 text-right">
                      <span className="text-green-500 font-bold text-xl">400 kr</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 text-slate-600 dark:text-slate-300">{t('pg.Tennis.41d594')}</td>
                    <td className="py-4 text-right">
                      <span className="text-green-500 font-bold text-xl">250 kr</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-slate-400 text-sm mt-4 text-center">
                {t('pg.Tennis.7943c0')}
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/pricing">
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider hover:bg-green-500/90">
                {t('pg.Tennis.1dc3c5')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold tracking-tighter text-slate-900 mb-4">
            {t('pg.Tennis.fbc3a6')}
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            {t('pg.Tennis.96245b')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/tennis/login">
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-500/90">
                <LogIn className="mr-2 w-5 h-5" />
                {t('pg.Tennis.f62c7f')}
              </Button>
            </Link>
            <a 
              href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-slate-300 text-slate-900 uppercase tracking-wider px-8 py-6 hover:bg-slate-100 dark:bg-slate-700">
                {t('pg.Tennis.8df769')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default Tennis;
